import { useTranslation } from 'react-i18next'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { useCurrentProject } from 'src/providers/current-project.provider'
import PlanningTable from './_components/planning-table/planning-table.component'
import { useEffect, useState } from 'react'
import PlanningFilters from './_components/planning-heading/planning-filters.component'
import PlanningTabs from './_components/planning-heading/planning-tabs.component'
import {
  TPlanningPatientMedEventsResponse,
  TPatientMedEventsByRangeOfDays,
} from './_components/planning.type'
import { useSession } from 'src/providers/auth.provider'
import PlanningPagination from './_components/planning-table/planning-pagination.component'
import { usePlanningState } from './_components/use-planning.hook'
import { useNavigate } from 'react-router-dom'
import { CurecallBanner } from '@honestica/curecall-ui'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'
import {
  AddPlanningTabModal,
  UpdateAllPlanningTabModal,
  UpdatePlanningTabModal,
} from './_components/planning-tab-modal/planning-tab-modal.component'
import PlanningTabDeleteModal from './_components/planning-tab-modal/planning-tab-delete-modal.component'
import PlanningRangeOfDays from './_components/planning-heading/planning-range-of-days.component'
import PageTitle from 'src/components/page-title/page-title.component'
import dayjs from 'dayjs'
import PlanningOtherFilters from './_components/planning-heading/planning-other-filters.component'
import { PlanningService } from 'src/services/planning.service'
import { getStringWithFirstLetterInUpperCase } from 'src/utils/string-formatter.util'
import { PlanningTourComponent } from './_components/planning-tour/planning-tour.component'
import { isAllPlanningTab } from './_components/planning-all-tab.util'

export default function PlanningPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { project, planningTabs, allPlanningTab, isFeatureActivated } = useCurrentProject()
  const { currentUser } = useSession()

  const canSeePatientPlanning = isFeatureActivated('is-planning-patient-activated')
  const {
    currentTab,
    currentFilters,
    currentOrder,
    currentPage,
    moveToTab,
    updatePlanningFilters,
    resetFilters,
    updateOrder,
    updatePageNumber,
    getApiParams,
  } = usePlanningState({ sortedTabs: planningTabs, allPlanningTab })

  const [showMigrationNote, setShowMigrationNote] = useState<boolean>(false)
  const [isAddPlanningTabModalOpen, setIsAddPlanningTabModalOpen] = useState<boolean>(false)
  const [isDuplicatePlanningTabModalOpen, setIsDuplicatePlanningTabModalOpen] = useState<boolean>(false)
  const [isUpdatePlanningTabModalOpen, setIsUpdatePlanningTabModalOpen] = useState<boolean>(false)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)

  useEffect(() => {
    if (currentTab.migrationNote && currentUser?.isAdmin) {
      setShowMigrationNote(true)
    } else {
      setShowMigrationNote(false)
    }
  }, [currentTab.migrationNote, currentUser?.isAdmin])

  const { data, isLoading, error } = useQuery<TPlanningPatientMedEventsResponse>({
    queryKey: [
      'planning-patient-med-events',
      currentTab.id,
      currentTab.columns,
      currentPage,
      currentOrder.orderBy,
      currentOrder.orderDirection,
      currentFilters.medEventIds,
      currentFilters.medicalStaffMemberIds,
      currentFilters.locationIds,
      currentFilters.relativeDaysFromToday,
    ],
    queryFn: () => PlanningService.getPatientMedEventsList(project.id, getApiParams()),
    enabled: !!canSeePatientPlanning,
    placeholderData: keepPreviousData, // avoid tab flickering when switching tabs
  })

  const { data: patientMedEventsByRangeOfDays } = useQuery<TPatientMedEventsByRangeOfDays>({
    queryKey: ['planning-patient-med-events-by-range-of-days', project.id, currentTab.id],
    queryFn: () => PlanningService.getPatientMedEventsByRangeOfDays(project.id, currentTab.id),
    enabled: !!canSeePatientPlanning,
    refetchInterval: 300000, // 5 minutes in milliseconds
  })

  // Redirect if user cannot see patient planning
  useEffect(() => {
    if (!canSeePatientPlanning) {
      const locale = i18n.language
      navigate(`/${locale}/${project.id}/medical`, { replace: true })
    }
  }, [canSeePatientPlanning, project.id, navigate, i18n.language])

  // Avoid user to have a wrong page number in the URL
  useEffect(() => {
    if (data?.totalPages && currentPage > data.totalPages) {
      updatePageNumber(data.totalPages)
    }
  }, [data?.totalPages, currentPage, updatePageNumber])

  if (error) return <ErrorQueryHandler error={error} />

  return (
    <div className="c-relative">
      <PlanningTourComponent />

      <PageTitle data-planning-tour="title" className="c-w-max">
        {t('pages.planning.title', {
          date: currentFilters.relativeDaysFromToday
            ? getStringWithFirstLetterInUpperCase(
                dayjs().add(currentFilters.relativeDaysFromToday, 'day').format('dddd D MMMM YYYY'),
              )
            : getStringWithFirstLetterInUpperCase(dayjs().format('dddd D MMMM YYYY')),
        })}
      </PageTitle>

      {showMigrationNote && (
        <CurecallBanner
          appearance="INFO"
          actionLabel="Cacher la note"
          action={() => setShowMigrationNote(false)}
        >
          <span className="c-flex c-items-center c-gap-2">
            <AdminOnlyBadge />
            {currentTab.migrationNote}
          </span>
        </CurecallBanner>
      )}

      <PlanningTabs
        sortedTabs={planningTabs}
        allTab={allPlanningTab}
        currentTab={currentTab}
        moveToTab={moveToTab}
        addTab={() => setIsAddPlanningTabModalOpen(true)}
        updateTab={() => setIsUpdatePlanningTabModalOpen(true)}
        duplicateTab={() => setIsDuplicatePlanningTabModalOpen(true)}
        deleteTab={() => setOpenDeleteModal(true)}
      />

      <div className="c-flex c-items-center c-gap-3 c-py-3 c-flex-wrap">
        <PlanningRangeOfDays
          planningTab={currentTab}
          currentFilters={currentFilters}
          updatePlanningFilters={updatePlanningFilters}
          patientMedEventsByRangeOfDays={patientMedEventsByRangeOfDays}
        />
        <hr className="c-border-l c-h-6 c-border-gray-500" />
        <div className="c-flex c-gap-3 c-items-center c-flex-shrink-0 c-flex-auto">
          <PlanningFilters
            currentFilters={currentFilters}
            updatePlanningFilters={updatePlanningFilters}
            resetFilters={resetFilters}
          />
          <PlanningOtherFilters
            currentFilters={currentFilters}
            updatePlanningFilters={updatePlanningFilters}
            resetFilters={resetFilters}
          />
        </div>
      </div>

      <PlanningTable
        patientMedEvents={data?.patientMedEvents}
        currentTabColumns={currentTab.columns}
        currentOrder={currentOrder}
        updateOrder={updateOrder}
        isLoading={isLoading}
        currentRelativeDay={currentFilters.relativeDaysFromToday}
        updatePlanningFilters={updatePlanningFilters}
      />
      <PlanningPagination
        currentPage={currentPage}
        updatePageNumber={updatePageNumber}
        patientMedEventsCount={data?.patientMedEventsCount}
        totalPages={data?.totalPages}
      />

      {openDeleteModal && !isAllPlanningTab(currentTab) && (
        <PlanningTabDeleteModal tabToDelete={currentTab} onClose={() => setOpenDeleteModal(false)} />
      )}

      {isUpdatePlanningTabModalOpen &&
        (isAllPlanningTab(currentTab) ? (
          <UpdateAllPlanningTabModal
            allPlanningTab={currentTab}
            onClose={() => setIsUpdatePlanningTabModalOpen(false)}
          />
        ) : (
          <UpdatePlanningTabModal
            planningTabToUpdate={currentTab}
            moveToTab={moveToTab}
            onClose={() => setIsUpdatePlanningTabModalOpen(false)}
          />
        ))}

      {isAddPlanningTabModalOpen && (
        <AddPlanningTabModal
          currentFilters={currentFilters}
          moveToTab={moveToTab}
          onClose={() => setIsAddPlanningTabModalOpen(false)}
        />
      )}

      {isDuplicatePlanningTabModalOpen && (
        <AddPlanningTabModal
          valuesToCopy={currentTab}
          currentFilters={currentFilters}
          moveToTab={moveToTab}
          onClose={() => setIsDuplicatePlanningTabModalOpen(false)}
        />
      )}
    </div>
  )
}
